/**
 * Legal consent API client.
 *
 * Contract (spec docs/specifications/2026-07-03-legal-consent-soft-modal/spec.md,
 * "Kontrakt API"):
 * - GET  /auth/me           -> user payload includes `legal: AuthMeLegal` (consumed
 *                              via HotelContext/getCurrentUser, not fetched here).
 * - POST /legal/accept       { documents: { documentType, version }[] } -> 204 | 400 LEGAL_VERSION_MISMATCH
 * - POST /legal/telemetry    { action, documents } -> 204 (shown/dismissed only; accept logs "accepted" itself)
 */
import apiClient from "./apiClient";
import type { LegalDocumentType, PendingDoc } from "@hotelb2b/legal-content";

/**
 * Re-exported from `@hotelb2b/legal-content` (ADR-075 SoT) to avoid drift (A1/M2):
 * `PendingDoc` is the same shape produced by `resolvePendingFromCache()` on the
 * backend (`/auth/me`'s `legal.pending`), so backend and frontend can never diverge.
 */
export type { LegalDocumentType, PendingDoc };

/** `GET /auth/me` -> `legal` field (soft modal, no `/legal/status` anymore). */
export interface AuthMeLegal {
  /** From LEGAL_CONSENT_MODAL_ENABLED — false means the frontend must skip the render entirely. */
  modalEnabled: boolean;
  /** Documents requiring re-acceptance (0-2). */
  pending: PendingDoc[];
  documents: Record<LegalDocumentType, { accepted: boolean; current: string }>;
}

export interface AcceptLegalDocument {
  documentType: LegalDocumentType;
  version: string;
}

export type LegalTelemetryAction = "shown" | "dismissed";

export interface LegalTelemetryDocument {
  documentType: LegalDocumentType;
  version: string;
}

export async function acceptLegal(
  documents: AcceptLegalDocument[],
): Promise<void> {
  await apiClient.post("/legal/accept", { documents });
}

/**
 * Log a `shown`/`dismissed` telemetry event (Loki, `context=LegalConsentTelemetry`).
 * Fire-and-forget from the caller's perspective; callers are responsible for
 * per-session dedupe (throttle/dedupe rules live server-side too, see spec).
 */
export async function postLegalTelemetry(
  action: LegalTelemetryAction,
  documents: LegalTelemetryDocument[],
): Promise<void> {
  await apiClient.post("/legal/telemetry", { action, documents });
}
